import type { ReactNode } from 'react';
import { ChatNote, ChatTurn } from '@wundo/ui';
import { renderAdditionalProperties } from '@/utils/messageUtils.ts';
import type { Message } from '@/types/messages.ts';
import { MessageBubble } from './parts/MessageBubble.tsx';
import { MessageFooter } from './parts/MessageFooter.tsx';
import { MessageReactions } from './parts/MessageReactions.tsx';
import { MetadataDump } from './parts/MetadataDump.tsx';
import { ToolCallTurn } from './parts/ToolCallTurn.tsx';
import { isKnowledgeBaseSearch } from './parts/knowledgeBase.ts';
import { useMessageUnfolding } from './parts/useMessageUnfolding.ts';

export interface MessageDisplayProps {
  message: Message;
  /**
   * Everything the message carried besides its words: the metadata dump, the
   * classification pills. What the console's "show internal messages" switch
   * turns on.
   */
  debug?: boolean;
  /** Shows the classification pills outside debug too. */
  showClassifications?: boolean;
  /** The line under the bubble: when it was said, and what became of it. */
  showMetadata?: boolean;
  /**
   * Says one of the message's choices as the user. Left out — a transcript
   * being read rather than a conversation being had — and every choice is
   * inert, which is what a recorded answer should be.
   */
  onButtonClick?: (title: string) => void;
  /** Whether a message is already on its way; a choice waits for it. */
  busy?: boolean;
  /** Opens this user's memories, with the handles this turn was offered. */
  onOpenMemories?: (offeredHandles: string[]) => void;
  /** Remarks about this message, set under it, such as a reviewer's findings. */
  notes?: ReactNode;
  /** The turn's element id, for a page that scrolls to it. */
  id?: string;
}

/**
 * One turn of a conversation, whatever kind of turn it is. It draws the messages
 * page, a reviewed conversation and a user's chat sessions alike: they are the
 * same records, differing only in whether their choices can still be answered.
 */
export function MessageDisplay({
  message,
  debug = false,
  showClassifications = false,
  showMetadata = true,
  onButtonClick,
  busy = false,
  onOpenMemories,
  notes,
  id,
}: MessageDisplayProps) {
  const unfolding = useMessageUnfolding(message);
  const fromUser = message.role === 'user' || message.type === 'incoming_message';
  const isAside = message.type === 'tool_call' || message.type === 'system_message';
  const metadata = debug ? renderAdditionalProperties(message) : null;

  const footer = showMetadata ? (
    <MessageFooter
      message={message}
      fromUser={fromUser}
      showClassifications={showClassifications || debug}
      showingOriginal={unfolding.showingOriginal}
      onToggleOriginal={unfolding.toggleOriginal}
      reasoningOpen={unfolding.reasoningOpen}
      onToggleReasoning={unfolding.toggleReasoning}
      onOpenMemories={onOpenMemories}
      flush={isAside}
    />
  ) : null;

  if (isAside) {
    return (
      <ChatTurn id={id} data-slot='message' data-kind={message.type}>
        <ToolCallTurn message={message} />
        {metadata && message.type !== 'tool_call' && !isKnowledgeBaseSearch(message) && (
          <MetadataDump>{metadata}</MetadataDump>
        )}
        {footer}
        {notes}
      </ChatTurn>
    );
  }

  return (
    <ChatTurn id={id} data-slot='message' data-kind={message.type}>
      <MessageBubble
        message={message}
        body={unfolding.body}
        fromUser={fromUser}
        metadata={metadata}
        onButtonClick={onButtonClick}
        answered={!onButtonClick || busy}
      />

      <MessageReactions reactions={message.reactions} fromUser={fromUser} />

      {footer}

      {unfolding.reasoning && unfolding.reasoningOpen && (
        <ChatNote author={fromUser ? 'user' : 'wundo'}>
          {unfolding.reasoning.join('\n\n')}
        </ChatNote>
      )}

      {notes}
    </ChatTurn>
  );
}
